/*----------------------------------
- HOME: THE SAUCER AND ITS FREE MONTH
----------------------------------*/

/**
 * The saucer of Home's hero and the window its catch opens (docs/mockups/website-home-v3.html `#ufo` and `#catch`,
 * wow.js "The saucer"), mounted inside the hero stage by HomeStage. It owns the device's record, the analytics and
 * the claim link; the parts (Saucer, SaucerModal) own the flight and the window.
 *
 * - The record is `localStorage ud-saucer`, read after mount only (the server renders the same nothing for everyone):
 *   `lost` is written the moment the window opens, `claimed` only by the claim, so the quiet link, Escape, a reload or
 *   a closed tab all lose the month, and a device with any record never sees the saucer again.
 * - `?saucer` clears the record and sends the saucer after 1.2s instead of 2.6s, for the journeys.
 * - The claim is an anchor to the Home signup link with `promo=saucer` (`source=website_landing_saucer_claim`); the
 *   website's conversion service opens the auth modal from it, and the intent carries the claim to Product.
 * - The saucer is the brand's render since Home v3 (docs/mockups/website-home-v3/ufo.png, 320x132 PNG, 58 KB), resized
 *   to 300x124 (2x the 150px the catch window draws it at; 96px in the sky) and re-encoded to WebP with its transparency
 *   in a Chromium canvas: 11 KB, lazy by nature (it only mounts after the motion gate).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule One catch per device, and the month lives in the modal only: opening it records the month as lost on this
 *       device, only the claim records it as claimed, and the saucer never flies again once either is stored
 *       (Gaetan, 2026-10-25: FOMO is the point).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useState } from 'preact/hooks';

import { SAUCER_PROMO } from '@uniquedomains/components/pricing/catalogs/promotions';
import { Saucer } from '@uniquedomains/components/marketing/saucer/Saucer';
import { SaucerModal, type SaucerLoss } from '@uniquedomains/components/marketing/saucer/SaucerModal';
import type { SaucerImage } from '@uniquedomains/components/marketing/saucer/saucerImage';
import UfoWebp from '@/client/assets/world/ufo-300x124.webp';
import useToast from '@/client/hooks/useToast';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';

/*----------------------------------
- TYPES
----------------------------------*/

/** What the device remembers; any value means the saucer is spent here. */
type SaucerRecord = 'lost' | 'claimed';

/** `reading` until mount has read the record; `flying` while the saucer may cross; `caught` with the window open. */
type SaucerPhase = 'reading' | 'flying' | 'caught' | 'spent';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SAUCER_STORAGE_KEY = 'ud-saucer';
const SAUCER_TEST_PARAM = 'saucer';
const TRIGGER_ID = 'website_landing_saucer_claim';

/** The tracking place of every saucer event: Home's hero, where the saucer flies. */
const TRACKING = { page: 'landing_home', section: 'hero_saucer', surface: 'landing_saucer' } as const;

/**
 * The Home signup link, cold like the hero's (no plan armed, no persona: the saucer asks nothing), carrying the claim.
 * Built once: it holds no visitor data.
 */
const SAUCER_CLAIM_HREF = buildWebsiteAuthHref({ flow: 'cold', mode: 'signup', from: TRIGGER_ID, promo: 'saucer' });

const SAUCER_IMAGE: SaucerImage = { src: UfoWebp, width: 300, height: 124 };

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * Storage is blocked in some private windows and with site data turned off, where every access throws a DOMException.
 * That is an expected visitor setting, not a fault: the read answers "no record" (the saucer flies as on a first
 * visit) and a write is skipped (the page keeps working; the month simply cannot be remembered on that device). Any
 * other error is a bug and is rethrown.
 */
const isBlockedStorage = (error: unknown): error is DOMException => error instanceof DOMException;

const readRecord = (): SaucerRecord | null => {
    try {
        const value = window.localStorage.getItem(SAUCER_STORAGE_KEY);
        return value === 'lost' || value === 'claimed' ? value : null;
    } catch (error) {
        if (isBlockedStorage(error)) return null;
        throw error;
    }
};

const writeRecord = (record: SaucerRecord | null): void => {
    try {
        if (record) window.localStorage.setItem(SAUCER_STORAGE_KEY, record);
        else window.localStorage.removeItem(SAUCER_STORAGE_KEY);
    } catch (error) {
        if (!isBlockedStorage(error)) throw error;
    }
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeSaucer = () => {
    const toast = useToast();
    const [phase, setPhase] = useState<SaucerPhase>('reading');
    const [flyNow, setFlyNow] = useState(false);

    useEffect(() => {
        const test = new URLSearchParams(window.location.search).has(SAUCER_TEST_PARAM);
        if (test) writeRecord(null);
        setFlyNow(test);
        setPhase(readRecord() ? 'spent' : 'flying');
    }, []);

    const onCatch = () => {
        // Written as the window opens, before any choice: leaving the page in any way now loses the month.
        writeRecord('lost');
        track('saucer_caught', { ...TRACKING });
        setPhase('caught');
    };

    const onClaim = () => {
        writeRecord('claimed');
        trackLandingCta({ ...TRACKING, label: SAUCER_PROMO.claim, kind: 'auth', href: SAUCER_CLAIM_HREF });
        trackConversionTrigger(TRIGGER_ID, { href: SAUCER_CLAIM_HREF, page: TRACKING.page, section: TRACKING.section });
        // The conversion service opened the auth modal from the anchor's href (capture phase); this window leaves.
        setPhase('spent');
    };

    const onLose = (via: SaucerLoss) => {
        track('saucer_lost', { ...TRACKING, via });
        toast.info(SAUCER_PROMO.lostToast);
        setPhase('spent');
    };

    if (phase === 'reading' || phase === 'spent') return null;

    return (
        <>
            <Saucer image={SAUCER_IMAGE} onCatch={onCatch} flyNow={flyNow} gone={phase !== 'flying'} testId="landing-saucer" />
            <SaucerModal image={SAUCER_IMAGE} open={phase === 'caught'} claimHref={SAUCER_CLAIM_HREF} onClaim={onClaim} onLose={onLose} testId="landing-saucer-modal" />
        </>
    );
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export default HomeSaucer;
